import { useTranslation } from 'next-i18next';
import {
  Button, LinkStyled, bjToast, Modal,
} from '@components/common';
import { useBjModal } from '@hooks/common/useBjModal';
import { IUserSubscriptionList, ApiUserSubscriptionPaymentDetailsOk } from '@type/v1-api-types/user-subscription';
import { fetcher, isApiV1Errors } from '@utils/data-fetching';
import { useExtendedRouter } from '@hooks/next-routing-wrappers';
import { showCloseTalentSubscriptionModal } from '@components/templates/employer/subscription/CloseTalentSubscriptionModal';
import { NeuroIcon } from '@components/common/Icons';
import { NextSubscriptionPayment } from './NextSubscriptionPayment';

// Interface
// *****************************************************
interface SubscriptionInfoProps {
  locale: Locale,
  currentSubscription?: IUserSubscriptionList;
  paymentDetails?: ApiUserSubscriptionPaymentDetailsOk;
}

type RequestType = 'closeRequest' | 'deleteCloseRequest';


/**
 * @description Subscription info component. It shows the current subscription info.
 */
export const TalentPremiumSubscriptionInfo = (props: SubscriptionInfoProps) => {
  // Destructure props
  const {
    locale,
    currentSubscription,
    paymentDetails,
  } = props;

  // Translation
  const { t } = useTranslation('common');

  const { router } = useExtendedRouter();

  // TODO: This is not the React way, the author should rewrite this
  const successCall = () => {
    router.refresh();
  };

  const callRequest = () => {
    let endpointUrl;

    const type: RequestType = currentSubscription?.closeRequest ? 'deleteCloseRequest' : 'closeRequest';

    switch (type) {
      case 'closeRequest':
        showCloseTalentSubscriptionModal(locale, currentSubscription, successCall);

        break;
      case 'deleteCloseRequest':
        endpointUrl = '/v1/user-subscription/delete-close-request';

        fetcher(endpointUrl, locale, {
          method: 'PUT',
          payload: undefined,
          stringifyPayload: true,
        }).then((response) => {
          if (isApiV1Errors(response)) {
            bjToast.error(response.errors[0].message);
          } else {
            successCall();
          }

          return null;
        }).catch(() => {});

        break;

      default:
        showCloseTalentSubscriptionModal(locale, currentSubscription, successCall);

        break;
    }
  };


  // Simplify variables for convenience
  // *****************************************************
  const renewalDate = currentSubscription?.nextChargeAt;
  const closeDate = currentSubscription?.closeRequest;
  const discountAmount = paymentDetails?.priceDiscount || 0;


  // Texts and colors for renewal/close date
  const colorRenewalExpiry = closeDate ? 'text-error' : 'text-ink-medium';
  const textRenewalExpiry = closeDate
    ? 'premium.talent.subscription.expirationDate'
    : 'premium.talent.subscription.renewalDate';
  const textNeuroAmount = `${t('global.label.discount')}: €${discountAmount} - `;


  // Current subscription modal
  // *****************************************************
  const { isOpen, toggleModal } = useBjModal();


  // Return null if there is no current package
  // *****************************************************
  if (!currentSubscription) return null;


  // Render component
  // *****************************************************
  return (
    <div className="container my-6 md:flex lg:my-9">

      {/* Subscription title */}
      <div className="md:grow">
        <div className="text-sm text-primary-light">
          {t('premium.talent.subscription.active')}
        </div>
        <h3 className="text-3xl font-bold tracking-tight">
          {currentSubscription?.package?.title}
        </h3>
      </div>

      {/* Subscription info */}
      <div className="my-3 divide-y divide-ink/15 md:my-0 md:flex md:items-center md:divide-x md:divide-y-0">

        {/* Neuro bonus */}
        <div className="flex items-center gap-2 pb-2.5 md:px-5 md:pb-0">
          <div className="flex size-9 items-center justify-center rounded-full bg-secondary text-white">
            <NeuroIcon />
          </div>
          <div>
            <div className="text-sm text-ink-medium">
              {t('subscription.neuro.discount.label')}
            </div>
            <div className="font-bold">
              <span className="text-ink">
                {textNeuroAmount}
              </span>
              <Button
                size="sm"
                color="primary-light"
                styling="text"
                onClick={toggleModal}
              >
                <span className="first-letter:uppercase">{t('global.label.see_details')}</span>
              </Button>
            </div>
          </div>
        </div>

        {/* Close request date (if we have one) */}
        {
          closeDate && (
            <div className="pt-2.5 md:px-5 md:pt-0">
              <div className={`text-sm ${colorRenewalExpiry}`}>
                {t('subscription.close-request-date.label')}
              </div>
              <div className="font-bold">
                {closeDate}
              </div>
            </div>
          )
        }

        {/* Current subscription renewal /expiry date, with relevant button */}
        <div className="pt-2.5 md:pl-5 md:pt-0">
          <div className={`text-sm ${colorRenewalExpiry}`}>
            {t(textRenewalExpiry)}
          </div>
          <div className="font-bold">
            {renewalDate}
            <span className="px-2">-</span>

            { /* Open cancel request modal */
              !closeDate && (
                <Button
                  size="sm"
                  styling="text"
                  rounding="full"
                  color="ink-medium"
                  className="!justify-start font-medium"
                  onClick={(event) => {
                    event.preventDefault();
                    callRequest();
                  }}
                >
                  {t('premium.talent.subscription.stop')}
                </Button>
              )
            }

            { /* Cancel close request. Shows only after the user cancels the subscription */
              closeDate && (
                <Button
                  styling="text"
                  rounding="none"
                  color="secondary"
                  className="!justify-start"
                  spinnerClass="mr-2 -my-2 h-5 w-5"
                  size="sm"
                  onClick={(event) => {
                    event.preventDefault();
                    callRequest();
                  }}
                >
                  {t('premium.talent.subscription.deleteCloseRequest')}
                </Button>
              )
            }

          </div>
        </div>

      </div>


      {/* Modal for current subscription */}
      <Modal
        isOpen={isOpen}
        onClose={toggleModal}
        size="sm"
        header={t('next-payment.label')}
        body={<NextSubscriptionPayment paymentDetails={paymentDetails} />}
      >
        <Modal.Footer className="!py-5">
          <LinkStyled
            href="/neuro"
            size="sm"
            color="primary-light"
            styling="text"
            className="!font-semibold underline"
          >
            {t('earn.neuro.for.premium.discount')}
          </LinkStyled>
        </Modal.Footer>
      </Modal>
    </div>
  );
};
